import type { KnowledgeUnit } from '../types'

// ---------------------------------------------------------------------------
// kpis.ts — curated KPI units
//
// Draft revision v0 (not yet analyst-greenlit per spec §18).
// Covers the lead KPIs for all 6 analytics pages; ratio vs. absolute noted.
// ---------------------------------------------------------------------------

export const kpis: KnowledgeUnit[] = [
  {
    id: 'kpi:sales_roi',
    type: 'kpi',
    keys: [
      'sales_roi',
      'sales roi',
      'return on investment',
      'roi',
      'efficiency',
      'profitability',
      'ratio',
    ],
    version: '0.1-draft',
    title: 'Sales RoI',
    summary:
      'Ratio KPI: net sales value attributable to media per unit of net spend. Lead KPI on Campaign Performance, Dynamics, and MultiDeepDive.',
    body: `Sales RoI (sales_roi) is a RATIO KPI — it requires the ratio-walk path (walk_ratio / walk_ratio_absolute), not the standard absolute walk.

v3 KPI encoding: \`sales_roi__<driver>__<value_type>\`
Default on Campaign Performance / Dynamics / MultiDeepDive: \`sales_roi__nsp__npv\`
  - driver = nsp (net spend is the denominator)
  - value_type = npv (net-present-value representation)

What it measures: how much net sales value the model attributes to media, per EUR of net spend.
Higher = better return. A value of 3.0 means EUR 3 of attributed sales per EUR 1 of spend.

It is NOT an absolute count or revenue total — it is a return ratio. Do not compare it to absolute sales figures directly.

Ratio KPI roots in MMMv3: sales_roi, sales_cpo, sales_opc, sales_mediashare — all use walk_ratio / walk_ratio_absolute.

Typical drivers: nsp (net spend) is the standard denominator driver.
Typical value_types: npv (default), possibly others depending on model configuration.

Pages: Campaign Performance (default), Dynamics (default), MultiDeepDive (default).`,
    seeAlso: ['concept:npv', 'kpi:nsp'],
  },

  {
    id: 'kpi:nsp',
    type: 'kpi',
    keys: ['nsp', 'net spend', 'spend', 'investment', 'budget', 'media spend'],
    version: '0.1-draft',
    title: 'NSP (Net Spend)',
    summary:
      'Absolute KPI: total net media investment/expenditure. Lead KPI on Marketing Invests. Also the denominator driver for sales_roi.',
    body: `NSP (Net Spend) is an ABSOLUTE KPI — it uses the standard absolute walk (walk_absolute / walk_timeline), not a ratio walk.

What it measures: total net media spend — the actual money invested across campaigns, channels, or time periods. Typically in currency units (EUR).

It is the lead KPI on the Marketing Invests page and answers spend-analysis questions.

NSP is also the denominator (driver) in the default \`sales_roi__nsp__npv\` encoding — meaning Sales RoI measures return per unit of net spend.

Do NOT use NSP alone to answer "was the campaign efficient?" — combine with sales_roi for that.

Typical use:
- "How much did we spend on Campaign X last quarter?" → nsp on Marketing Invests.
- "Which channel received the most budget?" → nsp with group-by on Marketing Invests.
- "What is the return per EUR of spend?" → sales_roi__nsp__npv on Campaign Performance.`,
    seeAlso: ['concept:cpo'],
  },

  {
    id: 'kpi:actual_sales',
    type: 'kpi',
    keys: ['actual_sales', 'actual sales', 'sales', 'revenue', 'observed sales', 'headline'],
    version: '0.2-draft',
    title: 'Actual Sales',
    summary:
      'Absolute KPI: total observed/realized sales (not model-attributed). Lead KPI on Review KPIs.',
    body: `Actual Sales (actual_sales) is an ABSOLUTE KPI — it reflects the total observed sales figure from the data, independent of model attribution.

What it measures: realized sales revenue or volume over the selected timeframe. This is the "ground truth" headline business metric — what actually happened, not what the model estimates.

Key distinction from kpi:sales:
- actual_sales = the observed total (fact table: kpi_overall); not decomposed by driver.
- sales (model attribution) = the model's decomposed sales attribution used in Contribution.

Subscription funnel (value_types): actual_sales is NOT a single observed total — it carries three value_types keyed \`grossadds\`, \`netsales\`, and \`topups\` (the stages of the subscription/sales funnel). These are taxonomy-level keys; their human display labels are tenant-specific vocabulary. Switch between them or co-plot them with the multi-select "Value Type" filter (set_value_types); an empty selection shows all. So "total sales" on Review KPIs is really a choice of which funnel stage to read.

Lead KPI on: Review KPIs (/review-status/review-kpis).

The Review KPIs page uses actual_sales to answer questions like:
- "How are overall sales performing?"
- "What were total sales this period vs. last period?"
- "Are we on track for target?"

It does NOT decompose sales by media vs. baseline — use Contribution for decomposition.`,
    seeAlso: ['kpi:sales'],
  },

  {
    id: 'kpi:sales',
    type: 'kpi',
    keys: [
      'sales',
      'sales attribution',
      'model sales',
      'decomposition',
      'contribution',
      'media',
      'units',
    ],
    version: '0.2-draft',
    title: 'Sales (model attribution)',
    summary:
      'Absolute KPI: model-attributed sales, used as the decomposition base on the Contribution page.',
    body: `Sales (sales) in the v3 KPI encoding is the model-attributed / decomposed sales figure — distinct from actual_sales (the observed total).

v3 KPI encoding: \`sales__media__units\` (the default on Contribution)
  - root = sales
  - driver = media (decomposed by media contribution)
  - value_type = units (volume)

Currency sibling: \`sales__media__EUR\` is the same root and driver expressed in the EUR value_type — currency-denominated media-driven sales — instead of units (volume). \`sales__media__units\` and \`sales__media__EUR\` are two selectable value_types of the one KPI; switch or co-plot them with the multi-select "Value Type" filter (set_value_types). Use units for volume questions, EUR for revenue questions.

What it measures: the model's attribution of total sales across drivers (media, baseline, etc.). Used to answer "what share of sales came from media vs. organic/baseline?"

Lead KPI on: Contribution (/review-performance/contribution).
Fact table: model_success_kpi.

On the Contribution page, sales is decomposed into stacked contributions:
- Media contribution (the part attributable to media spend)
- Baseline / non-media contribution

This is NOT the same as actual_sales — model_success_kpi attributions may not sum exactly to actual_sales due to model calibration.

To see the raw observed sales total, use actual_sales on Review KPIs.`,
    seeAlso: ['concept:mediashare'],
  },
]
